import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, logoutUser as apiLogout, register as apiRegister } from '../services/api';

const AuthContext = createContext();

// Session duration constants (in milliseconds)
const SESSION_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days (much longer session)
const IDLE_TIMEOUT = 7 * 24 * 60 * 60 * 1000; // 7 days of inactivity
const WARNING_BEFORE_EXPIRY = 60 * 60 * 1000; // Show warning 60 minutes before expiry

// Constants for security checks
const TEST_USER_ID = '67fcfb75a18dc80702b9fb4c';
const TEST_USER_EMAIL = 'user@test.com';

export const useAuth = () => {
  return useContext(AuthContext);
};

// Safe wrapper to prevent user switching
const SafeAuthWrapper = ({ children, currentUser }) => {
  // Disabled SafeAuth to prevent looping issues
  // Original implementation was causing excessive re-renders and console logs
  return <>{children}</>;
};

// Validate user to prevent test user from being used with wrong email
const validateUser = (user) => {
  if (!user || !user._id) {
    console.error('Invalid user object:', user);
    return false;
  }
  
  // If this is the test user ID, it must have the test email
  if (user._id === TEST_USER_ID && user.email !== TEST_USER_EMAIL) {
    console.error('SECURITY: Test user ID with wrong email!', user.email);
    return false;
  }
  
  return true;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState(null);
  
  // Check token validity based on its timestamp
  const isTokenValid = () => {
    // Always consider token valid if it exists - let the server validate
    return !!localStorage.getItem('token');
  };
  
  // Check if user has been active recently
  const isSessionActive = () => {
    // Always consider session active if token exists - let the server decide
    return !!localStorage.getItem('token');
  };
  
  // Update the last activity timestamp
  const updateActivityTimestamp = () => {
    const now = new Date().getTime();
    localStorage.setItem('lastActivityTime', now.toString());
  };
  
  // Extend the current session
  const extendSession = () => {
    const now = new Date().getTime();
    localStorage.setItem('tokenTimestamp', now.toString());
    localStorage.setItem('lastActivityTime', now.toString());
    setSessionWarning(false);
    console.log('Session extended');
  };
  
  // Calculate time until session expiry
  const calculateTimeUntilExpiry = () => {
    const tokenTimestamp = localStorage.getItem('tokenTimestamp');
    if (!tokenTimestamp) {
      // If we have a token but no timestamp, set one now
      if (localStorage.getItem('token')) {
        const now = new Date().getTime();
        localStorage.setItem('tokenTimestamp', now.toString());
        return SESSION_DURATION;
      }
      return 0;
    }
    
    const timestamp = parseInt(tokenTimestamp, 10);
    const now = new Date().getTime();
    
    // Calculate when session will expire
    const expiryTime = timestamp + SESSION_DURATION;
    return Math.max(0, expiryTime - now);
  };

  // Set up activity tracking
  useEffect(() => {
    // Update activity timestamp on user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleUserActivity = () => {
      if (isAuthenticated) {
        updateActivityTimestamp();
        
        // If there was a session warning, check if we need to clear it
        if (sessionWarning) {
          const timeLeft = calculateTimeUntilExpiry();
          
          // If user activity has pushed expiry beyond warning threshold, clear warning
          if (timeLeft > WARNING_BEFORE_EXPIRY) {
            setSessionWarning(false);
          }
        }
      }
    };
    
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [isAuthenticated, sessionWarning]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      // Check for existing token and user data (try localStorage first, then sessionStorage)
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
      const sessionUser = sessionStorage.getItem('currentSessionUser');
      
      // Reduce logging to prevent console spam
      // console.log('Initializing auth context, token exists:', !!token);
      
      if (token && userData) {
        try {
          // Parse the user data
          const parsedUser = JSON.parse(userData);
          
          // Log the user data for debugging only on dev environments
          if (process.env.NODE_ENV === 'development') {
            console.log('User session restored:', parsedUser.name);
          }
          
          // If we have a sessionUser ID for this tab that differs from localStorage
          if (sessionUser && sessionUser !== parsedUser._id) {
            console.warn('Session user ID differs from localStorage user ID');
            console.log('Session user:', sessionUser);
            console.log('localStorage user:', parsedUser._id);
            
            // Trust the sessionStorage - it's specific to this browser tab
            // But we want to maintain the user details, just update the ID
            parsedUser._id = sessionUser;
            
            // Update localStorage to maintain consistency
            localStorage.setItem('user', JSON.stringify(parsedUser));
            console.log('Updated localStorage user to match sessionStorage user ID');
          }
          // If no sessionUser but we have a user ID, set it in sessionStorage
          else if (!sessionUser && parsedUser._id) {
            sessionStorage.setItem('currentSessionUser', parsedUser._id);
            // console.log('Set session user ID from localStorage:', parsedUser._id);
          }
          
          // Validate user integrity using our validation function
          if (!validateUser(parsedUser)) {
            console.error('Failed user validation during initialization');
            clearAuthData();
            setLoading(false);
            return;
          }
          
          // Store user ID in localStorage for convenience
          if (parsedUser._id) {
            localStorage.setItem('userId', parsedUser._id);
          }
          
          setCurrentUser(parsedUser);
          setIsAuthenticated(true);
          
          // Ensure timestamps exist
          const now = new Date().getTime();
          if (!localStorage.getItem('tokenTimestamp')) {
            localStorage.setItem('tokenTimestamp', now.toString());
          }
          if (!localStorage.getItem('lastActivityTime')) {
            localStorage.setItem('lastActivityTime', now.toString());
          }
          
          // Update activity timestamp
          updateActivityTimestamp();
          
          // Validate token with server (optional - only if needed)
          // For now, trust the stored token and let API calls handle validation
          console.log('User session restored:', parsedUser.name);
        } catch (error) {
          console.error('Error restoring user session:', error);
          clearAuthData(); // Clear invalid session data
        }
      } else {
        // console.log('No valid token found, clearing auth data');
        clearAuthData();
      }
      
      setLoading(false);
    };
    
    // Call the initialization function
    initializeAuth();
  }, []);
  
  // Helper to clear all auth data
  const clearAuthData = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenTimestamp');
    localStorage.removeItem('lastActivityTime');
    sessionStorage.removeItem('currentSessionUser');
    setCurrentUser(null);
    setIsAuthenticated(false);
    setSessionWarning(false);
  };
  
  // Check session validity periodically (disabled for now to avoid auto-logout)
  useEffect(() => {
    const checkSession = () => {
      // Only show warning, don't auto-logout
      if (isAuthenticated) {
        const timeLeft = calculateTimeUntilExpiry();
        setTimeUntilExpiry(timeLeft);
        
        // Show warning when session is about to expire
        if (timeLeft <= WARNING_BEFORE_EXPIRY && !sessionWarning) {
          setSessionWarning(true);
        } else if (timeLeft > WARNING_BEFORE_EXPIRY && sessionWarning) {
          setSessionWarning(false);
        }
        
        // Check for agency status updates in localStorage
        checkForAgencyStatusUpdates();
      }
    };
    
    const intervalId = setInterval(checkSession, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, sessionWarning]);
  
  // Check for updates to agency status in localStorage
  const checkForAgencyStatusUpdates = () => {
    try {
      if (currentUser) {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const storedUser = JSON.parse(userJson);
          
          // Check if there's an agency status update
          if (storedUser.agencyId && 
              (!currentUser.agencyStatus || 
               currentUser.agencyStatus !== storedUser.agencyStatus)) {
            console.log('Agency status changed in localStorage - updating context');
            
            // Check if any fields actually changed to prevent unnecessary re-renders
            const needsUpdate = 
              currentUser.agencyId !== storedUser.agencyId ||
              currentUser.agencyStatus !== storedUser.agencyStatus ||
              currentUser.agencyName !== storedUser.agencyName ||
              currentUser.position !== storedUser.position ||
              currentUser.agencyRequestDate !== storedUser.agencyRequestDate;
              
            if (needsUpdate) {
              // Update the current user with the new agency status
              setCurrentUser({
                ...currentUser,
                agencyId: storedUser.agencyId,
                agencyName: storedUser.agencyName,
                agencyStatus: storedUser.agencyStatus,
                position: storedUser.position,
                agencyRequestDate: storedUser.agencyRequestDate
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('Error checking for agency status updates:', err);
    }
  };
  
  // Add a shorter interval check specifically for agency status
  useEffect(() => {
    if (isAuthenticated) {
      // Significantly reduce the frequency of agency status checks to minimize performance impact
      const checkInterval = setInterval(checkForAgencyStatusUpdates, 5 * 60 * 1000); // Check every 5 minutes instead
      
      // Initial check on mount
      checkForAgencyStatusUpdates();
      
      return () => clearInterval(checkInterval);
    }
  }, [isAuthenticated]); // Remove currentUser._id from dependencies to prevent re-creating interval

  const login = async (email, password) => {
    try {
      // Clear any existing errors
      clearAuthData();
      
      // Clear any existing redirection flags
      sessionStorage.removeItem('isRedirecting');
      
      // Prepare login data
      let loginData;
      if (typeof email === 'object' && email !== null) {
        loginData = email;
      } else if (typeof email === 'string' && typeof password === 'string') {
        loginData = { email, password };
      } else {
        throw new Error('Invalid login credentials format');
      }
      
      // Attempt connection using the standard API
      try {
        console.log('Attempting normal login via API service');
        const response = await apiLogin(loginData);
        const { token, user } = response;
        
        // Validate user integrity
        if (!validateUser(user)) {
          throw new Error('Authentication error: Invalid user data received from server');
        }
        
        console.log('Login successful, storing session data');
        
        // Store token and user data in both localStorage and sessionStorage for better persistence
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(user));
        
        // Store user ID for easier access
        if (user._id) {
          localStorage.setItem('userId', user._id);
        }
        
        // Store in sessionStorage for tab-specific identity
        if (user._id) {
          sessionStorage.setItem('currentSessionUser', user._id);
        }
        
        // Store the login timestamp
        const now = new Date().getTime();
        localStorage.setItem('tokenTimestamp', now.toString());
        localStorage.setItem('lastActivityTime', now.toString());
        
        // Set authentication state - IMPORTANT: this triggers the navigation
        setCurrentUser(user);
        setIsAuthenticated(true);
        setSessionWarning(false);
        
        return true;
      } catch (error) {
        console.error('API login error:', error);
        
        // If we're on localhost or in development, provide a mock login option
        if (window.location.hostname === 'localhost' || 
            window.location.hostname === '127.0.0.1' ||
            process.env.NODE_ENV === 'development') {
          
          console.log('Using development fallback login');
          
          // Only accept the test account credentials
          if (loginData.email === 'user@test.com' && loginData.password === 'password123') {
            const testUser = {
              _id: '67fcfb75a18dc80702b9fb4c',
              name: 'Test User',
              email: 'user@test.com',
              role: 'super_admin' // Give admin privileges for testing
            };
            
            console.log('Development login successful, storing test user data');
            
            // Store token and user data
            localStorage.setItem('token', 'test-token-for-development');
            localStorage.setItem('user', JSON.stringify(testUser));
            
            // Set session data
            sessionStorage.setItem('currentSessionUser', testUser._id);
            
            // Set timestamps
            const now = new Date().getTime();
            localStorage.setItem('tokenTimestamp', now.toString());
            localStorage.setItem('lastActivityTime', now.toString());
            
            // Update state - IMPORTANT: this triggers the navigation
            setCurrentUser(testUser);
            setIsAuthenticated(true);
            setSessionWarning(false);
            
            console.log('Development login successful for:', testUser.email);
            return true;
          } else {
            throw new Error('Invalid credentials. For testing, use user@test.com / password123');
          }
        } else {
          // For production, try an alternate API endpoint
          try {
            // On aeroindustrialhub.com, try a direct API connection (with proper CORS)
            if (window.location.hostname === 'aeroindustrialhub.com') {
              // Use the more reliable /api endpoint on the same origin
              console.log('Trying alternative approach');
              
              const response = await fetch(`${window.location.origin}/api/auth/login`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(loginData)
              });
              
              if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
              }
              
              const data = await response.json();
              
              // Store token and user data
              localStorage.setItem('token', data.token);
              localStorage.setItem('user', JSON.stringify(data.user));
              
              // Store user ID for easier access
              if (data.user._id) {
                localStorage.setItem('userId', data.user._id);
              }
              
              // Store in sessionStorage for tab-specific identity
              if (data.user._id) {
                sessionStorage.setItem('currentSessionUser', data.user._id);
              }
              
              // Store the login timestamp
              const now = new Date().getTime();
              localStorage.setItem('tokenTimestamp', now.toString());
              localStorage.setItem('lastActivityTime', now.toString());
              
              // Set authentication state
              setCurrentUser(data.user);
              setIsAuthenticated(true);
              setSessionWarning(false);
              
              return true;
            }
          } catch (fallbackError) {
            console.error('Alternative login approach failed:', fallbackError);
          }
          
          // If we get here, all approaches have failed
          throw error;
        }
      }
    } catch (error) {
      // Clear any partial auth data on error
      clearAuthData();
      sessionStorage.removeItem('isRedirecting');
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Clear local storage
      clearAuthData();
      
      // Call API logout if enabled
      try {
        await apiLogout();
      } catch (error) {
        console.error('Error during API logout:', error);
        // Continue with client-side logout
      }
      
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      return false;
    }
  };

  // Update user data
  const updateUser = async (userData) => {
    try {
      console.log('AuthContext updateUser received data:', {
        address: userData.address,
        addressType: typeof userData.address,
        addressLength: userData.address ? userData.address.length : 'undefined',
        fullUserData: userData
      });
      
      // Validate the user data
      if (!validateUser(userData)) {
        console.error('Invalid user data in updateUser');
        return false;
      }

      // Ensure address field is properly handled
      const updatedUserData = {
        ...userData,
        address: typeof userData.address === 'string' ? userData.address : '', // Preserve spaces, just ensure it's a string
        phone: userData.phone || '',     // Ensure phone is never undefined
      };
      
      console.log('AuthContext updatedUserData after processing:', {
        address: updatedUserData.address,
        addressType: typeof updatedUserData.address,
        addressLength: updatedUserData.address ? updatedUserData.address.length : 'undefined'
      });

      // Update localStorage
      const currentData = JSON.parse(localStorage.getItem('user') || '{}');
      const mergedData = {
        ...currentData,
        ...updatedUserData,
        // Preserve these fields if they exist in current data
        agencyId: currentData.agencyId || updatedUserData.agencyId,
        agencyStatus: currentData.agencyStatus || updatedUserData.agencyStatus,
        agencyName: currentData.agencyName || updatedUserData.agencyName,
      };
      
      localStorage.setItem('user', JSON.stringify(mergedData));
      
      // Update state
      setCurrentUser(mergedData);
      
      // Log successful update
      console.log('User data updated successfully:', {
        name: mergedData.name,
        phone: mergedData.phone,
        address: mergedData.address
      });
      
      return true;
    } catch (error) {
      console.error('Error updating user:', error);
      return false;
    }
  };
  
  // Force refresh of user data from localStorage
  const refreshUserFromStorage = () => {
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        if (userData && userData._id) {
          // Ensure required fields are present
          const normalizedData = {
            ...userData,
            address: typeof userData.address === 'string' ? userData.address : '', // Preserve spaces
            phone: userData.phone || '',
          };
          
          // Only update state if data has changed to prevent unnecessary re-renders
          const needsUpdate = !currentUser || 
            currentUser._id !== userData._id ||
            currentUser.email !== userData.email ||
            currentUser.name !== userData.name ||
            currentUser.role !== userData.role ||
            currentUser.agencyId !== userData.agencyId ||
            currentUser.agencyStatus !== userData.agencyStatus ||
            currentUser.address !== userData.address ||  // Check address changes
            currentUser.phone !== userData.phone;        // Check phone changes
            
          if (needsUpdate) {
            setCurrentUser(normalizedData);
            console.log('User data refreshed with updates:', {
              name: normalizedData.name,
              phone: normalizedData.phone,
              address: normalizedData.address
            });
          }
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error('Error refreshing user from storage:', err);
      return false;
    }
  };

  // Utility function to check authentication status (for debugging)
  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const sessionUser = sessionStorage.getItem('currentSessionUser');
    
    console.log('Auth Status Check:', {
      token: !!token,
      userData: !!userData,
      sessionUser,
      currentUser: currentUser?._id,
      isAuthenticated
    });
    
    return {
      hasToken: !!token,
      hasUserData: !!userData,
      sessionUser,
      currentUser: currentUser?._id,
      isAuthenticated
    };
  };

  // Check if current user can make bookings (directly, as admin)
  const canMakeBookings = () => {
    return currentUser && (currentUser.role === 'agent_admin' || currentUser.role === 'super_admin');
  };

  // Check if current user can request bookings (as agency member)
  const canRequestBookings = () => {
    // Super admins can always request bookings
    if (currentUser && currentUser.role === 'super_admin') {
      return true;
    }
    
    // If user is an agent admin, they can always request bookings
    if (currentUser && currentUser.role === 'agent_admin') {
      return true;
    }
    
    // Check localStorage for most up-to-date agency status
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        if (userData && userData.agencyId && userData.agencyStatus === 'approved') {
          // Update current user if needed
          if (!currentUser.agencyStatus || currentUser.agencyStatus !== 'approved') {
            setCurrentUser({
              ...currentUser,
              agencyId: userData.agencyId,
              agencyStatus: 'approved',
              agencyName: userData.agencyName || 'Agency'
            });
          }
          return true;
        }
      }
    } catch (err) {
      console.error('Error checking booking permission from localStorage:', err);
    }
    
    // Fall back to current user state
    return currentUser && currentUser.agencyId && currentUser.agencyStatus === 'approved';
  };

  // Check if user is an agency member
  const isAgencyMember = () => {
    // Super admins and agent admins are considered agency members for permission purposes
    if (currentUser && (currentUser.role === 'super_admin' || currentUser.role === 'agent_admin')) {
      return true;
    }
    
    // Check for real-time status
    try {
      // First check localStorage in case it's more up-to-date
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        if (userData && userData.agencyId && userData.agencyStatus === 'approved') {
          // If currentUser is outdated, update it
          if (!currentUser.agencyStatus || currentUser.agencyStatus !== 'approved') {
            console.log('Agency status in localStorage is approved but not in currentUser, updating');
            setCurrentUser({...currentUser, agencyStatus: 'approved', agencyId: userData.agencyId});
          }
          return true;
        }
      }
    } catch (err) {
      console.error('Error checking real-time agency status:', err);
    }
    
    // Fall back to context state
    return currentUser && currentUser.agencyId && currentUser.agencyStatus === 'approved';
  };
  
  // Check if user has a pending agency request
  const hasPendingAgencyRequest = () => {
    // Check for real-time status
    try {
      // First check localStorage in case it's more up-to-date
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        if (userData && userData.agencyId && userData.agencyStatus === 'pending') {
          return true;
        }
      }
    } catch (err) {
      console.error('Error checking real-time pending agency request:', err);
    }
    
    // Fall back to context state
    return currentUser && currentUser.agencyId && currentUser.agencyStatus === 'pending';
  };

  // Check if user is an agent admin
  const isAgentAdmin = () => {
    return currentUser && currentUser.role === 'agent_admin';
  };

  // Check if user is a super admin
  const isSuperAdmin = () => {
    return currentUser && currentUser.role === 'super_admin';
  };

  // Check if user is any type of admin
  const isAdmin = () => {
    return currentUser && (currentUser.role === 'admin' || currentUser.role === 'super_admin');
  };

  const getUserId = () => {
    // Try to get the ID from current user first
    if (currentUser && currentUser._id) {
      // Also save it to localStorage for convenience
      localStorage.setItem('userId', currentUser._id);
      return currentUser._id;
    }
    
    // If not available in current state, try localStorage
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      return storedUserId;
    }
    
    // If still not found, check user object in localStorage
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        if (user && user._id) {
          localStorage.setItem('userId', user._id);
          return user._id;
        }
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
    
    // If still not found, check session storage
    const sessionUser = sessionStorage.getItem('currentSessionUser');
    if (sessionUser) {
      localStorage.setItem('userId', sessionUser);
      return sessionUser;
    }
    
    // No user ID found
    return null;
  };

  // Add register function
  const register = async (name, email, password, phone, address) => {
    try {
      const userData = { name, email, password, phone, address };
      const response = await apiRegister(userData);
      
      if (response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        // Set up session timestamps
        const now = new Date().getTime();
        localStorage.setItem('tokenTimestamp', now.toString());
        localStorage.setItem('lastActivityTime', now.toString());
        
        // Update state
        setCurrentUser(response.user);
        setIsAuthenticated(true);
        
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const checkAuthStatusInterval = setInterval(checkAuthStatus, 30 * 60 * 1000); // Check every 30 minutes
    
    return () => clearInterval(checkAuthStatusInterval);
  }, []);

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    sessionWarning,
    timeUntilExpiry,
    login,
    logout,
    register,
    updateUser,
    refreshUser: refreshUserFromStorage,
    extendSession,
    clearAuthData,
    canMakeBookings,
    canRequestBookings,
    isAgencyMember,
    hasPendingAgencyRequest,
    isAgentAdmin,
    isSuperAdmin,
    isAdmin,
    getUserId,
    checkAuthStatus,
  };

  // Wrap the provider value with our SafeAuth component
  return (
    <AuthContext.Provider value={value}>
      <SafeAuthWrapper currentUser={currentUser}>
        {children}
      </SafeAuthWrapper>
    </AuthContext.Provider>
  );
};

export default AuthProvider; 